import React, { useMemo } from 'react';
import ReactPlayer from 'react-player';
import styled from 'styled-components';
import {
  CpHeading03Primary,
  CpBody01Secondary,
  CpBody03Primary,
  CpBody03Placeholder,
} from 'components/atoms/customer-portal/typography';
import { useGetBackground } from 'components/organisms/customer-portal/types/utils';
import { CpSectionWrapper } from '../../common';
import { CpVideosVariantWidgetProps } from '../types';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  align-items: flex-start;
  overflow-y: hidden;
  gap: var(--cp-spacing-08);

  @media (max-width: 480px) {
    gap: 0;
  }
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-03);
`;

const Title = styled(CpHeading03Primary)`
  margin-bottom: 0;
`;

const Description = styled(CpBody03Placeholder)`
  margin-bottom: 0;
`;

export const VideoListVerticalVideoStackWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--cp-spacing-06);

  @media (max-width: 480px) {
    gap: 0;
  }
`;

export const VideoListVerticalVideoStackWrapperVideoCard = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: var(--cp-spacing-09);

  .videoPlayer {
    width: 40%;
  }

  @media (max-width: 480px) {
    flex-direction: column;
    gap: 24px;
    padding: 16px 0;
    .videoPlayer {
      min-width: 100%;
      height: 200px;
    }
  }
`;

const VideoTitle = styled(CpBody03Primary)`
  margin-bottom: 0;
`;

const VideoDescription = styled(CpBody01Secondary)`
  margin-bottom: 0;
`;

export const VideoListVerticalVideoStackWrapperVideoDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--cp-spacing-05);
  width: 100%;
`;

export const CpVerticalVideoStack: React.FC<CpVideosVariantWidgetProps> = ({
  disabled,
  data,
  widget,
}) => {
  const background = useGetBackground(widget);

  const resources = useMemo(
    () =>
      data?.resources?.filter((e) => {
        return (
          !!e?.imageLink && !!e?.name?.trim?.()
        );
      }) ?? [],
    [data?.resources],
  );

  return (
    <CpSectionWrapper
      paddingY={widget?.widgetDefinition?.styles?.paddingY as number}
      isDisabled={disabled}
      background={background}
      data-builder-mode-full-tile="true"
    >
      <Wrapper>
        <TitleWrapper>
          <Title
            data-cy="cp-video-list-vertical-video-stack-title"
            autoAdjustColor
          >
            {data.title ?? 'Videos'}
          </Title>
          <Description
            data-cy="cp-video-list-vertical-video-stack-description"
            autoAdjustColor
          >
            {data.description}
          </Description>
        </TitleWrapper>
        <VideoListVerticalVideoStackWrapper data-cy="cp-video-list-vertical-video-stack">
          {resources?.map((metric, index) => (
            <VideoListVerticalVideoStackWrapperVideoCard
              data-cy={`cp-video-list-vertical-video-stack-card-${index}`}
            >
              <div className="videoPlayer">
                <ReactPlayer
                  url={metric.imageLink}
                  width="100%"
                  height="100%"
                  controls
                />
              </div>

              <VideoListVerticalVideoStackWrapperVideoDetails
                data-cy={`cp-video-list-vertical-video-stack-details-${index}`}
              >
                <VideoTitle
                  autoAdjustColor
                  data-cy={`cp-video-list-vertical-video-stack-title-${index}`}
                >
                  {metric.name}
                </VideoTitle>
                <VideoDescription
                  autoAdjustColor
                  data-cy={`cp-video-list-vertical-video-stack-description-${index}`}
                >
                  {metric.description}
                </VideoDescription>
              </VideoListVerticalVideoStackWrapperVideoDetails>
            </VideoListVerticalVideoStackWrapperVideoCard>
          ))}
        </VideoListVerticalVideoStackWrapper>
      </Wrapper>
    </CpSectionWrapper>
  );
};
